import {
  addDays,
  format,
  isPast,
  isToday,
  isTomorrow,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import toastService from "utils/toast";
import { IMember } from "../TeamSettings/types";
import { IMemberList } from "../../components/MemberSelection/types";
import { Board, GroupByTypeEnum, IPriority, SprintStatus } from "./types";

import {
  BoardType,
  ICustomizeColumns,
  ISeverity,
  ITicketStatuses,
} from "app/types";
import {
  FieldCategoryTypeEnum,
  ITicketTypes,
  TicketField,
} from "../TeamSettings/container/Projects/container/WorkItems/types";
import { severityOptionsData } from "./components/CreateBoardsComponent/data";
import {
  IAddTaskButton,
  ITicketFormData,
  ITickets,
  TicketGroup,
} from "./containers/Tickets/types";
import { priorityOptions, TICKET_HEADERS } from "./data";

export const getSprintStatusClasses = (status: SprintStatus) => {
  switch (status) {
    case SprintStatus.CURRENT:
      return "bg-success100 text-success";
    case SprintStatus.PREVIOUS:
      return "bg-danger100 text-danger";
    case SprintStatus.NEXT:
      return "bg-purple100 text-purple";
    default:
      return "";
  }
};

export const getProjectIdFromCurrentURL = () => {
  const url = window.location.href;
  const pathSegments = new URL(url).pathname.split("/");
  return (
    pathSegments.find((segment) => /^[0-9a-fA-F-]{36}$/.test(segment)) || null
  );
};

export const getCurrentUserOwnerIds = (
  currentUserId?: string,
  projectMembers: IMemberList[] = [],
): Set<string> => {
  const ownedIds = new Set<string>();
  if (!currentUserId) {
    return ownedIds;
  }

  ownedIds.add(currentUserId);
  projectMembers.forEach((member) => {
    if (member._id === currentUserId || member.userId === currentUserId) {
      if (member._id) {
        ownedIds.add(member._id);
      }
      if (member.userId) {
        ownedIds.add(member.userId);
      }
    }
  });

  return ownedIds;
};

export const filterMyBoards = (
  boards: Board[],
  currentUserId?: string,
  projectMembers: IMemberList[] = [],
): Board[] => {
  const ownedIds = getCurrentUserOwnerIds(currentUserId, projectMembers);
  if (!currentUserId) {
    return [];
  }

  return boards.filter(
    (board) => board.owner?._id && ownedIds.has(board.owner._id),
  );
};

export const getBoardLink = (
  board,
  projectId?: string,
  teamId?: string,
): string => {
  const baseUrl = window.location.origin;
  let newPath = "";

  if (board.Type === BoardType.Backlog) {
    newPath = `/app/${projectId}/${teamId}/boards/${board._id}/backlog`;
  } else {
    newPath = `/app/${projectId}/${teamId}/boards/${board._id}/${board?.viewType}/board`;
  }

  return `${baseUrl}${newPath}`;
};

export const handleCopyBoardLink = (
  board,
  projectId?: string,
  teamId?: string,
) => {
  const boardLink = getBoardLink(board, projectId, teamId);
  navigator.clipboard
    .writeText(boardLink)
    .then(() => {
      toastService.success(`${board.name} link has been successfully copied.`);
    })
    .catch(() => {
      toastService.error("Failed to copy board link");
    });
};

export const handleRedirectToBoard = (
  board,
  projectId?: string,
  teamId?: string,
) => {
  const boardLink = getBoardLink(board, projectId, teamId);
  window.location.href = boardLink;
};

export const getDueDateGroupName = (dueDateStr: string): string => {
  if (!dueDateStr) return "No Due Date";
  const today = startOfDay(new Date());
  const dueDate = parseISO(dueDateStr);

  if (isPast(dueDate) && !isToday(dueDate)) {
    return "Past Due";
  }

  if (isToday(dueDate)) {
    return "Today";
  }

  if (isTomorrow(dueDate)) {
    return "Tomorrow";
  }
  const intervalStart = today;
  const intervalEnd = addDays(today, 6);

  if (isWithinInterval(dueDate, { start: intervalStart, end: intervalEnd })) {
    return format(dueDate, "EEEE"); // Monday, Tuesday, etc.
  }

  return "Future Date";
};

export const getEffortGroupName = (
  value: string | number | null | undefined,
  type: GroupByTypeEnum,
): string => {
  if (!value || value === "0") {
    if (type === GroupByTypeEnum.EFFORTS_ESTIMATION) {
      return "No Estimation";
    } else if (type === GroupByTypeEnum.EFFORT_ACTUAL) {
      return "No Actual Effort";
    }
  }
  let hours = 0;
  if (typeof value === "string") {
    const parts = value
      .split("/")
      .map(Number)
      .filter((n) => !isNaN(n));
    hours = parts.length === 2 ? (parts[0] / parts[1]) * 8 : parseFloat(value);
  } else if (typeof value === "number") {
    hours = value;
  }

  if (hours <= 5) return "1–5 hours";
  if (hours <= 20) return "6–20 hours";
  return ">20 hours";
};

export function formatLabel(str) {
  return str
    ?.replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before capital letters
    ?.replace(/^./, (char) => char.toUpperCase()); // Capitalize first letter
}

export const addTicketsLocally = (
  group: TicketGroup,
  allTickets: ITickets[],
) => {
  const groupTicketRefs = group?.ticketsList || [];
  const groupTicketIds = new Set(groupTicketRefs.map((t) => t?._id));
  const allTicketMap = new Map(
    allTickets.map((ticket) => [ticket?._id, ticket]),
  );
  const resolvedTickets: ITickets[] = [];
  let maxOrder = 0;
  // Resolve existing group ticket references
  for (const item of groupTicketRefs) {
    const ticket = allTicketMap.get(item?._id);
    if (ticket) {
      const ticketOrder = item.order ?? 0;
      resolvedTickets.push({
        ...ticket,
        order: ticketOrder,
      });
      maxOrder = Math.max(maxOrder, ticketOrder);
    }
  }
  // Add missing tickets from allTickets (based on groupId)
  const additionalTickets = allTickets
    .filter(
      (ticket) =>
        ticket?._id === group?._id && !groupTicketIds.has(ticket?._id),
    )
    .map((ticket: any, idx: number) => ({
      ...ticket,
      order: maxOrder + idx + 1,
    }));
  return {
    _id: group?._id,
    name: group?.name || "",
    tickets: [...resolvedTickets, ...additionalTickets],
    isCollapse: group?.isCollapse ?? false,
    isVisible: group?.isVisible ?? true,
    order: group?.order ?? 0,
    ticketLimit: group?.ticketLimit,
  };
};

export const getDefaultTicketFields = (
  group: TicketGroup,
  groupBy: string,
  members: IMember[] = [],
): Record<string, any> => {
  const groupValue = group?.name || "";

  const fieldMap: Record<string, any> = {
    status: { status: groupValue },
    priority: { priority: groupValue },
    dueDate: { dueDate: groupValue },
    assignedTo: (() => {
      // find member by name or id matching groupValue
      const match = members.find(
        (m) =>
          m.name.toLowerCase() === groupValue.toLowerCase() ||
          m._id === groupValue,
      );
      return match ? { assignedTo: match._id } : {};
    })(),
  };

  return fieldMap[groupBy] || {};
};

export const getGroupDefaultTicketFields = (
  group: TicketGroup,
  groupBy: string,
  members: IMember[] = [],
): Record<string, any> => {
  const groupValue = group?.name || "";

  const fieldMap: Record<string, any> = {
    status: { status: groupValue },
    priority: { priority: groupValue },
    dueDate: { dueDate: groupValue },
    assignedTo: (() => {
      // find member by name or id matching groupValue
      const match = members.find(
        (m) =>
          m.name.toLowerCase() === groupValue.toLowerCase() ||
          m._id === groupValue,
      );
      return match ? { assignedTo: match._id } : {};
    })(),
  };

  return fieldMap[groupBy] || {};
};

export function useContainerWidth() {
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateWidth = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerWidth(rect.width);
    }
  }, []);

  useEffect(() => {
    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [updateWidth]);

  return { containerWidth, containerRef };
}

export function getUpdatedColumns(data: ICustomizeColumns[]) {
  return data
    ?.filter((field) => {
      if (field.type === GroupByTypeEnum.TICKET_NAME) return true;
      if (
        field.type === GroupByTypeEnum.TAGS ||
        field.type === GroupByTypeEnum.TICKET_ID
      )
        return false;
      return !field.disabled && field.isVisible;
    })
    .map((field) => {
      const existingColumn = TICKET_HEADERS.find(
        (col) => col.name === field.fieldName,
      );

      return (
        existingColumn || {
          name:
            field.type === GroupByTypeEnum.TICKET_NAME
              ? "Name"
              : field.fieldName,
          id: field.type,
          resizable: true,
          className: "!p-[4px] text-xs leading-[18px] font-medium",
          width: field.type === GroupByTypeEnum.TICKET_NAME ? 428 : 100,
        }
      );
    });
}

export function getStatusesByTicketTypes(
  statuses: ITicketStatuses[],
  selectedIds: string | string[],
): ITicketStatuses[] {
  if (!Array.isArray(statuses) || !selectedIds) return [];

  const ids = Array.isArray(selectedIds) ? selectedIds : [selectedIds];

  return statuses.filter((status) =>
    status.ticketTypes.some((t) => ids.includes(t)),
  );
}
export function getStatusById(
  statuses: ITicketStatuses[],
  id: string,
): ITicketStatuses | undefined {
  if (!id) return undefined;

  return statuses.find((m) => m._id === id);
}

export function findFieldGroupByCategory(
  ticketTypeFields: TicketField[] = [],
  category: FieldCategoryTypeEnum,
): TicketField[] {
  if (!ticketTypeFields.length) return [];
  return ticketTypeFields.filter((g) => g.fieldCategory === category);
}

export function findDefaultBoardTicketType(
  ticketTypes: ITicketTypes[],
  type?: string,
): string | undefined {
  if (!ticketTypes?.length) return undefined;

  const resolvedType = type?.trim() || ticketTypes[0]._id;

  return ticketTypes.find((t) => t._id === resolvedType)?._id;
}
export function findDefaultBoardTypeName(
  ticketTypes: ITicketTypes[],
  type?: string,
): string | undefined {
  if (!ticketTypes?.length) return "Task";

  const resolvedType = type?.trim() || ticketTypes[0]._id;

  return ticketTypes.find((t) => t._id === resolvedType)?.name || "Task";
}

export const getTicketCustomFieldValue = (
  fieldId: any,
  form: ITicketFormData,
) => {
  const customField = form?.customFields?.find(
    (field) => field.fieldId === fieldId,
  );
  return customField?.value || "";
};
export const getTicketCustomFieldError = (
  fieldId: any,
  form: ITicketFormData,
) => {
  const customField = form?.customFields?.find(
    (field) => field.fieldId === fieldId,
  );
  return customField?.error || "";
};

export function getPriorityByIds(id: string): IPriority | undefined {
  if (!id) return undefined;

  return priorityOptions.find((m) => m.value === id);
}
export function getSeverityByIds(id: string): ISeverity | undefined {
  if (!id) return undefined;

  return severityOptionsData.find((m) => m.id === id);
}

export const fetchNoMatchGroupBy = (
  groupBy: GroupByTypeEnum,
  group: TicketGroup,
): TicketGroup => {
  if (group?._id && group._id.length > 0) {
    return group;
  }

  const noMatchMap: Record<GroupByTypeEnum, string> = {
    [GroupByTypeEnum.NONE]: "No group",
    [GroupByTypeEnum.TICKET_NAME]: "No ticket name",
    [GroupByTypeEnum.TICKET_ID]: "No ticket id",
    [GroupByTypeEnum.STATUS]: "No status",
    [GroupByTypeEnum.TAGS]: "No tags",
    [GroupByTypeEnum.EPIC]: "No epic",
    [GroupByTypeEnum.ASSIGNEE]: "No assignee",
    [GroupByTypeEnum.TICKET_TYPE]: "No ticket type",
    [GroupByTypeEnum.DUE_DATE]: "No due date",
    [GroupByTypeEnum.PRIORITY]: "No priority",
    [GroupByTypeEnum.SPRINT]: "No sprint",
    [GroupByTypeEnum.RELEASE]: "No release",
    [GroupByTypeEnum.TARGET_RELEASE]: "No target release",
    [GroupByTypeEnum.ENVIRONMENT]: "No environment",
    [GroupByTypeEnum.SEVERITY]: "No severity",
    [GroupByTypeEnum.COMPONENT]: "No component",
    [GroupByTypeEnum.CREATOR]: "No creator",
    [GroupByTypeEnum.CREATED_DATE]: "No created date",
    [GroupByTypeEnum.EFFORTS_ESTIMATION]: "No effort estimated",
    [GroupByTypeEnum.EFFORT_ACTUAL]: "No effort actual",
    [GroupByTypeEnum.CUSTOM_TITLES]: "No custom title",
  };

  return {
    ...group,
    name: noMatchMap[groupBy] || "No data",
  };
};

export const shouldShowAddTaskIcon = (
  showAddTaskIcon: IAddTaskButton,
  groupId: string,
  subGroupId?: string,
): boolean => {
  if (!showAddTaskIcon?.actions) return false;

  // ✅ If subgroup present → check inside same group + subgroup
  if (subGroupId) {
    return (
      showAddTaskIcon.groupId === groupId &&
      showAddTaskIcon.subGroupId === subGroupId
    );
  }

  // ✅ No subgroup → only group check
  return showAddTaskIcon.groupId === groupId;
};

export function chunkArray<T>(array: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}
